/**
 * Login Page Loader
 * Checks authentication state and tracks page load
 */

import { redirect, type LoaderFunctionArgs } from 'react-router-dom';
import {
  trackRouteLoad,
  trackRouteError,
  startRouteTimer,
  calculateDataSize,
} from '@/utils/route-analytics';

export interface LoginData {
  redirectUrl?: string;
  message?: string;
}

/**
 * Login loader function
 * Redirects to dashboard if already authenticated
 */
export async function loginLoader(args: LoaderFunctionArgs) {
  const getElapsed = startRouteTimer();

  try {
    // Check if user is already logged in (use correct storage key from keycloakAuthService)
    const authCredentials = localStorage.getItem('auth_credentials');
    if (authCredentials) {
      try {
        const credentials = JSON.parse(authCredentials);
        if (credentials.access_token && credentials.refresh_token) {
          // User is already authenticated, redirect to dashboard
          throw redirect('/dashboard');
        }
      } catch {
        // Invalid credentials, continue to login
      }
    }

    // Get any redirect URL from search params (for post-login redirect)
    const url = new URL(args.request.url);
    const redirectUrl = url.searchParams.get('redirect') ?? undefined;
    const message = url.searchParams.get('message') ?? undefined;

    const data: LoginData = {
      redirectUrl,
      message,
    };

    // Track performance
    const loadTime = getElapsed();
    const dataSize = calculateDataSize(data);
    trackRouteLoad('/login', loadTime, dataSize);

    // Return data directly (not wrapped)
    return data;
  } catch (error) {
    // If it's a redirect Response, let it propagate
    if (error instanceof Response) {
      throw error;
    }

    trackRouteError('/login', error as Error);
    throw error;
  }
}
